import axios from 'axios'
import qs from 'qs'
import { message } from 'antd'
import { store } from '../redux'
import { logout, setLoginShow } from '@/redux/modules/user/action'
import envCfg from '@/config/env'

// 是否正在刷新token
let isRefreshing = false
// 重试队列
let requestsQueue = []

/**
 * 创建axios实例
 */
const service = axios.create({
	baseURL: envCfg[process.env.REACT_APP_ENV]['API_BASE_URL'],
	timeout: 90000, // 优化：从600秒改为30秒，提升用户体验
	headers: {
		'Content-Type': 'application/json;charset=UTF-8'
	},
	// 允许携带凭证
	withCredentials: false
})

/**
 * 请求拦截器
 */
service.interceptors.request.use(
	(config) => {
		// 添加请求ID用于追踪
		config.metadata = { startTime: new Date().getTime() }

		// 获取token
		const token = store.getState().user?.token
		if (token && !config.headers['no-token']) {
			config.headers.Authorization = 'Bearer ' + token
		}

		// 添加请求ID（用于调试）
		config.headers['X-Request-ID'] = `${Date.now()}-${Math.random().toString(36).substring(7)}`

		// 优化：只对需要实时数据的API禁用缓存
		// 大部分列表、分类等数据可以利用浏览器缓存，减少服务器压力
		const NO_CACHE_APIS = [
			'/user/info',           // 用户信息需要实时
			'/message/',            // 消息相关需要实时
			'/notification/',       // 通知相关需要实时
			'/unread',              // 未读数需要实时
		]

		const needNoCache = NO_CACHE_APIS.some(api => config.url?.includes(api))

		if (config.method?.toUpperCase() === 'GET' && needNoCache) {
			config.params = {
				...config.params,
				_t: Date.now()
			}
		}

		// 处理表单数据
		if (Object.values(config.headers).includes('application/x-www-form-urlencoded')) {
			config.data = qs.stringify(config.data)
		}

		// 优化：仅在需要时打印请求日志，减少控制台输出
		// 可通过环境变量 REACT_APP_ENABLE_REQUEST_LOG=true 开启
		if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_ENABLE_REQUEST_LOG === 'true') {
			console.log(
				`%c[API Request] ${config.method?.toUpperCase()} ${config.url}`,
				'color: #4CAF50; font-weight: bold',
				{
					params: config.params,
					data: config.data,
					headers: config.headers
				}
			)
		}

		return config
	},
	(error) => {
		console.error('[Request Error]:', error)
		return Promise.reject(error)
	}
)

/**
 * 响应拦截器
 */
service.interceptors.response.use(
	(response) => {
		// 计算请求耗时
		const duration = new Date().getTime() - response.config.metadata.startTime

		// 开发环境打印响应信息
		if (process.env.NODE_ENV === 'development') {
			console.log(
				`%c[API Response] ${response.config.method?.toUpperCase()} ${response.config.url} (${duration}ms)`,
				'color: #2196F3; font-weight: bold',
				response.data
			)
		}

		// 特殊处理：文件下载
		if (response.config.url === "/api/homePage/downloadModel" || response.config.responseType === 'blob') {
			return response
		}

		const res = response.data

		// 响应成功
		if (res.code === 0 || res.code === 200) {
			return res
		}

		// 业务错误处理
		handleBusinessError(res)

		return res
	},
	async (error) => {
		// 网络错误
		if (!error.response) {
			console.error('[Network Error]:', error)
			message.error('网络连接失败，请检查网络设置')
			return Promise.reject(error)
		}

		const { response, config } = error
		const status = response?.status

		// 开发环境打印错误信息
		if (process.env.NODE_ENV === 'development') {
			console.error(
				`%c[API Error] ${config.method?.toUpperCase()} ${config.url}`,
				'color: #f44336; font-weight: bold',
				{
					status,
					statusText: response?.statusText,
					data: response?.data
				}
			)
		}

		// HTTP状态码错误处理
		await handleHttpError(status, error)

		return Promise.reject(error)
	}
)

/**
 * 处理业务错误
 */
const handleBusinessError = (res) => {
	// Mock模式下不处理认证错误
	if (process.env.REACT_APP_USE_MOCK === 'true') {
		return
	}

	const errorMessages = {
		400: '请求参数错误',
		401: '未登录或登录已过期',
		403: '没有权限访问该资源',
		404: '请求的资源不存在',
		500: '服务器内部错误',
		502: '网关错误',
		503: '服务暂时不可用',
		504: '网关超时'
	}

	// 认证错误：未登录或登录过期
	if (res.code === 401 || res.code === 403) {
		handleAuthError()
		return
	}

	// 显示错误消息
	const errorMsg = res.msg || errorMessages[res.code] || '操作失败'
	message.error(errorMsg)
}

/**
 * 处理HTTP错误
 */
const handleHttpError = async (status, error) => {
	// Mock模式下不处理认证错误
	if (process.env.REACT_APP_USE_MOCK === 'true') {
		return
	}

	switch (status) {
		case 401:
		case 403:
			// 认证失败
			handleAuthError()
			break

		case 404:
			message.error('请求的资源不存在')
			break

		case 500:
			message.error('服务器错误，请稍后再试')
			break

		case 502:
			message.error('网关错误，请稍后再试')
			break

		case 503:
			message.error('服务暂时不可用，请稍后再试')
			break

		case 504:
			message.error('请求超时，请稍后再试')
			break

		default:
			if (error.message.includes('timeout')) {
				message.error('请求超时，请检查网络连接')
			} else if (error.message.includes('Network Error')) {
				message.error('网络连接失败，请检查网络设置')
			} else {
				message.error(error.response?.data?.msg || '请求失败，请稍后再试')
			}
	}
}

/**
 * 处理认证错误
 */
const handleAuthError = () => {
	// Mock模式下不显示登录框
	if (process.env.REACT_APP_USE_MOCK === 'true') {
		return
	}

	const userState = store.getState().user

	// 如果已登录，则退出登录
	if (userState.isLogin) {
		message.error('登录已过期，请重新登录')
		store.dispatch(logout())
	}

	// 显示登录框
	store.dispatch(setLoginShow(true))
}

/**
 * Token刷新机制（预留，需要后端支持）
 */
const refreshToken = async () => {
	// TODO: 实现token刷新逻辑
	// const refreshToken = store.getState().user?.refreshToken
	// const res = await axios.post('/auth/refresh', { refreshToken })
	// return res.data.token
}

/**
 * 请求重试
 */
const retryRequest = (error, retries = 3) => {
	const config = error.config

	if (!config || !config.retry) {
		return Promise.reject(error)
	}

	config.__retryCount = config.__retryCount || 0

  if (config.__retryCount >= retries) {
		return Promise.reject(error)
	}

	config.__retryCount += 1

	const backoff = new Promise((resolve) => {
		setTimeout(() => {
			resolve()
		}, config.retryDelay || 1000)
	})

	return backoff.then(() => {
		return service(config)
	})
}

// 导出axios实例
export default service

/**
 * 请求辅助函数
 */
export const request = {
	get: (url, params, config = {}) => {
		return service.get(url, { params, ...config })
	},

	post: (url, data, config = {}) => {
		return service.post(url, data, config)
	},

	put: (url, data, config = {}) => {
		return service.put(url, data, config)
	},

	delete: (url, params, config = {}) => {
		return service.delete(url, { params, ...config })
	},

	upload: (url, file, onProgress) => {
		const formData = new FormData()
		formData.append('file', file)

		return service.post(url, formData, {
			headers: {
				'Content-Type': 'multipart/form-data'
			},
			onUploadProgress: (progressEvent) => {
				if (onProgress) {
					const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
					onProgress(percentCompleted)
				}
			}
		})
	}
}
